import React, { lazy, Suspense, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'

import type { RetorikMainComponentConfiguration } from '../../models/retorikTypes'
import { ContainerParent, Mode } from '../../models/enums'
import getThemeColors from '../../utils/getThemeColors'
import { useRetorikUI } from '../CustomAdaptiveCard'

import ContextProvider from '../Contexts/ContextProvider'
import RetorikComposer from './RetorikComposer'
import RetorikRouter from './RetorikRouter'
import Container from './Container'
import NewsView from '../Views/NewsView'
import SpeechManager from '../Speech/SpeechManager'
import FocusManager from '../Speech/FocusManager'
import MobileLandscapePreventer from '../Utils/MobileLandscapePreventer'
import SendTypingIndicator from '../Utils/SendTypingIndicator'
import ChatbotContainer from '../ChatbotAndSubtitles/ChatbotAndSubtitlesContainer'
import VeilleContainer from '../Veille/VeilleContainer'

const VirtualKeyboard = lazy(() =>
  Promise.resolve(import('@davi-ai/virtual-keyboard')).then((module) => ({
    default: module.default ?? module
  }))
)

// Overload adaptive card rendering classes
useRetorikUI()

interface RetorikContainerProps extends RetorikMainComponentConfiguration {
  parent: number
}

const RetorikContainer = ({
  config,
  viewsConfig,
  veilleConfig,
  mapConfig,
  chatbotData,
  chatbotDataWidget,
  agentData,
  ponyfillFactoryCredentials,
  addressData,
  userData,
  skipLoader,
  defaultMode,
  customVoice,
  externalEventHandler,
  externalComponents,
  width,
  height,
  colors,
  parent,
  enableVirtualKeyboard
}: RetorikContainerProps): JSX.Element => {
  const dispatch = useDispatch()
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const currentLayout = useSelector((state: RootState) => state.retorikReducer.currentLayout)
  const processedAgentData = useSelector((state: RootState) => state.retorikReducer.agentData)

  useEffect(() => {
    dispatch(storeActions.view.setThemeColors(getThemeColors(colors)))
  }, [])

  return config && viewsConfig && veilleConfig && mapConfig && chatbotData && chatbotDataWidget && agentData && ponyfillFactoryCredentials ? (
    <ContextProvider
      skipLoader={skipLoader}
      mode={defaultMode || Mode.vocal}
      viewsConfiguration={viewsConfig}
      veilleConfiguration={veilleConfig}
      mapConfiguration={mapConfig}
      configuration={config}
      addressData={addressData}
      agentSource={agentData}
      ponyfillFactoryCredentials={ponyfillFactoryCredentials}
      customVoice={customVoice}
    >
      {!config.forceMobileView && <MobileLandscapePreventer />}
      <RetorikComposer addressData={addressData} userData={userData} externalEventHandler={externalEventHandler} isRetorikNews={parent === ContainerParent.news}>
        <SpeechManager isRetorikNews={parent === ContainerParent.news} />
        <FocusManager />
        <Container fullSize={parent !== ContainerParent.widget && config.fullSize !== false} width={width} height={height} parent={parent}>
          {processedAgentData && (
            <ChatbotContainer centered={chatbotData.fullWidthInDesktopMode} chatbotData={currentLayout === ContainerParent.agent ? chatbotData : chatbotDataWidget} parent={parent} />
          )}
          {parent === ContainerParent.news ? <NewsView hideMenu={true} isRetorikNews={true} /> : <RetorikRouter externalComponents={externalComponents} />}
          <VeilleContainer />
          {enableVirtualKeyboard && (
            <Suspense>
              <VirtualKeyboard locale={locale} onlyOnTactileDevices={true} onlyWithRetorikFramework={true} />
            </Suspense>
          )}
        </Container>
        <SendTypingIndicator isRetorikNews={parent === ContainerParent.news} />
      </RetorikComposer>
    </ContextProvider>
  ) : (
    <React.Fragment />
  )
}

export default RetorikContainer
